import { useState } from 'react';
import {
  indirizzoAllegato,
  useAllegatiOrdine,
  useAttaccaFile,
  useTogliAllegato,
} from '../api/allegati-ordine.js';
import { ErroreApi } from '../api/cliente.js';
import { peso } from '../api/documenti.js';
import { ZonaFile } from '../componenti/ZonaFile.js';
import { data } from '../formati.js';
import { t } from '../i18n/lingua.js';

/**
 * Gli allegati dell'ordine.
 *
 * Nel v2 sono una scheda a sé con la sua barra azioni, e per caricarne uno si
 * fanno quattro passaggi: nuovo, titolo, salva, upload. Qui si trascina il
 * file e basta — il titolo, se non lo si scrive, è il nome del file senza
 * estensione, che è quello che uno scriverebbe comunque.
 *
 * Non c'è filtro sul tipo: nell'archivio vero ci sono `.msg` di Outlook,
 * fogli di calcolo e archivi zip. Limitare ai PDF vorrebbe dire che quella
 * roba torna a girare per e-mail.
 */
export function Allegati({ ordineId, archiviato }: { ordineId: number; archiviato: boolean }) {
  const allegati = useAllegatiOrdine(ordineId);
  const attacca = useAttaccaFile(ordineId);
  const togli = useTogliAllegato(ordineId);

  const [file, setFile] = useState<File | null>(null);
  const [titolo, setTitolo] = useState('');
  const [problema, setProblema] = useState<string | null>(null);
  /** Il secondo clic conferma: cancellare un allegato non si annulla. */
  const [daTogliere, setDaTogliere] = useState<number | null>(null);

  const elenco = allegati.data?.attachments ?? [];

  const manda = () => {
    if (!file) return;
    setProblema(null);
    attacca.mutate(
      { file, title: titolo.trim() || undefined },
      {
        onSuccess: () => {
          setFile(null);
          setTitolo('');
        },
        onError: (errore) =>
          setProblema(
            errore instanceof ErroreApi ? errore.message : t('Il caricamento non è riuscito.'),
          ),
      },
    );
  };

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">{t('Allegati')}</span>
        <span className="dim" style={{ marginLeft: 'auto', fontSize: 11 }}>
          {elenco.length > 0 && `${elenco.length}`}
        </span>
      </div>
      <div className="card-body">
        {elenco.length === 0 ? (
          <p className="dim" style={{ margin: '0 0 10px', fontSize: 13 }}>
            {allegati.isLoading ? t('Un momento…') : t('Nessun allegato su questo ordine.')}
          </p>
        ) : (
          <ul className="doclist">
            {elenco.map((a) => (
              <li key={a.id}>
                {a.fileSize === null ? (
                  // la riga c'è ma il file no: dirlo, invece di dare un link
                  // che scarica una pagina di errore
                  <span>{a.title}</span>
                ) : (
                  <a
                    className="cell-link"
                    href={indirizzoAllegato(ordineId, a.id)}
                    download={a.downloadName}
                  >
                    {a.title}
                  </a>
                )}
                <span className="dim">{data(a.createdAt)}</span>
                {a.fileSize === null ? (
                  <span className="badge badge-warn">{t('file non trovato')}</span>
                ) : (
                  <span className="dim">{peso(a.fileSize)}</span>
                )}
                {!archiviato &&
                  (daTogliere === a.id ? (
                    <>
                      <button
                        className="btn btn-ghost"
                        onClick={() => setDaTogliere(null)}
                        style={{ marginLeft: 'auto' }}
                      >
                        {t('Lascia stare')}
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          togli.mutate(a.id);
                          setDaTogliere(null);
                        }}
                      >
                        {t('Togli davvero')}
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-ghost"
                      style={{ marginLeft: 'auto' }}
                      onClick={() => setDaTogliere(a.id)}
                    >
                      {t('Togli')}
                    </button>
                  ))}
              </li>
            ))}
          </ul>
        )}

        {!archiviato && (
          <div style={{ marginTop: 12 }}>
            <ZonaFile file={file} massimo={30 * 1024 * 1024} onFile={setFile} />
            {file && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input
                  className="input"
                  style={{ flex: 1 }}
                  placeholder={t('Titolo (senza, vale il nome del file)')}
                  value={titolo}
                  onChange={(e) => setTitolo(e.target.value)}
                />
                <button className="btn btn-primary" disabled={attacca.isPending} onClick={manda}>
                  {attacca.isPending ? t('Carico…') : t('Attacca')}
                </button>
              </div>
            )}
            {problema && (
              <p className="dim" style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--tomato)' }}>
                {problema}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
